import React from "react";
import {
  TextField,
  Button,
  Grid,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import ModalLayout from "components/ModalLayout";
import MDButton from "components/MDButton";
import CancelButton from "components/CancelButton";

function CategoryFormModal({
  open,
  handleClose,
  categoryData,
  handleInputChange,
  handleFileChange,
  handleSubmit,
  isEditing,
  categories,
  serviceTypes,
  services,
  customForms,
}) {
  const selectedCategories = categoryData.parent_id
    ? categories.filter((category) => categoryData.parent_id.includes(category.id))
    : [];

  const selectedServices = (categoryData.service_ids || [])
    .map((id) => services.find((service) => service.id === id))
    .filter(Boolean);

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title={isEditing ? "Edit Sub Category" : "Add Sub Category"}
      subtitle="Sub Category"
      maxWidth={960}
      headerContent={
        <Typography variant="body2" color="text.secondary" mt={1}>
          Map this sub category to service types, services, and categories while managing pricing and
          assets.
        </Typography>
      }
      actions={
        <>
          <CancelButton onClick={handleClose} />
          <MDButton
            type="submit"
            form="sub-category-form"
            variant="gradient"
            color="info"
            sx={{ minWidth: 140 }}
          >
            {isEditing ? "Update" : "Add"}
          </MDButton>
        </>
      }
    >
      <form id="sub-category-form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={serviceTypes}
              getOptionLabel={(option) => option?.name_en || ""}
              value={
                categoryData.service_type_id
                  ? serviceTypes.find((serviceType) => serviceType.id === categoryData.service_type_id) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleInputChange({ target: { name: "service_type_id", value: newValue ? newValue.id : null } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Service Type" required InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }} />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={customForms}
              getOptionLabel={(option) => option?.name || ""}
              value={
                categoryData.form_id
                  ? customForms.find((customForm) => customForm.id === categoryData.form_id) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleInputChange({ target: { name: "form_id", value: newValue ? newValue.id : null } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Form" required InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }} />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              options={categories}
              getOptionLabel={(option) => option?.name_en || ""}
              value={selectedCategories}
              onChange={(event, newValue) => {
                handleInputChange({
                  target: {
                    name: "parent_id",
                    value: newValue.map((item) => item.id),
                  },
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Categories"
                  InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              options={services}
              getOptionLabel={(option) => option?.name_en || ""}
              value={selectedServices}
              onChange={(event, newValue) => {
                handleInputChange({ target: { name: "service_ids", value: newValue.map((service) => service.id) } });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Services"
                  variant="outlined"
                  InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title (EN)"
              name="name_en"
              value={categoryData.name_en}
              onChange={handleInputChange}
              margin="normal"
              required
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title (FR)"
              name="name_fr"
              value={categoryData.name_fr}
              onChange={handleInputChange}
              margin="normal"
              required
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Description (EN)"
              name="description_en"
              value={categoryData.description_en}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Description (FR)"
              name="description_fr"
              value={categoryData.description_fr}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Min Price"
              name="min_price"
              value={categoryData.min_price}
              onChange={handleInputChange}
              margin="normal"
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Price Per Km"
              name="price_per_km"
              value={categoryData.price_per_km}
              inputMode="decimal"
              onKeyDown={(event) => {
                if (["e", "E", "+", "-"].includes(event.key)) {
                  event.preventDefault();
                }
              }}
              onChange={(event) => {
                const { value } = event.target;
                if (value === "" || /^\d*(\.\d*)?$/.test(value)) {
                  handleInputChange({ target: { name: "price_per_km", value } });
                }
              }}
              margin="normal"
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(categoryData.double_location)}
                  onChange={(e) =>
                    handleInputChange({
                      target: { name: "double_location", value: e.target.checked, type: "checkbox" },
                    })
                  }
                  color="primary"
                />
              }
              label="Double Location"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="file"
                id="file-input"
                onChange={(e) => handleFileChange(e, "image")}
                style={{ display: "none" }}
              />
              <label htmlFor="file-input">
                <Button variant="outlined" component="span" sx={{ width: "100%", color: "gray", my: 2 }}>
                  Upload Image
                </Button>
              </label>
              {categoryData.image_preview && (
                <img
                  src={categoryData.image_preview}
                  alt="Preview"
                  style={{ width: "100px", height: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}
                />
              )}
            </div>
          </Grid>
        </Grid>
      </form>
    </ModalLayout>
  );
}

export default CategoryFormModal;
